/**
 * FeatureSection — 상세 섹션 공통 레이아웃 (Figma 2828:1982 계열)
 *
 * 시안의 상세 4개는 텍스트 컬럼 구조가 완전히 같고 좌우만 교대한다.
 * 그래서 레이아웃은 이 파일 하나로 두고 내용은 features.ts 배열이 공급한다.
 *
 * 반응형: 좌우 2단 → 좁은 화면에서 1단(이미지 아래 텍스트). 그 외는 시안 고정.
 * 색은 landing.css의 --lp-* 변수만 쓴다 (D-07 §1.2 랜딩페이지 예외).
 */
import type { Feature } from './features';

interface Props {
  feature: Feature;
  onStart: () => void;
}

/** before(회색) / after(파랑) 카드 — 아이콘 원 + 문단 */
function Point({ tone, children }: { tone: 'before' | 'after'; children: string }) {
  const isBefore = tone === 'before';
  return (
    <div
      className={`flex w-full items-start gap-3 rounded-xl p-4 ${
        isBefore ? 'bg-[var(--lp-before-surface)]' : 'bg-[var(--lp-after-surface)]'
      }`}
    >
      <span
        className={`mt-1 flex size-5 shrink-0 items-center justify-center rounded-full ${
          isBefore ? 'bg-[var(--lp-before-text)]' : 'bg-[var(--lp-brand)]'
        }`}
      >
        <img src="/landing/ic-check.svg" alt="" className="size-3" />
      </span>
      <p
        className={`min-w-0 flex-1 text-base leading-[1.6] ${
          isBefore
            ? 'text-[var(--lp-before-text)]'
            : 'font-bold text-[var(--lp-text-muted)]'
        }`}
      >
        {children}
      </p>
    </div>
  );
}

export function FeatureSection({ feature, onStart }: Props) {
  const { id, kicker, headline, before, after, image, reverse, background } = feature;

  return (
    <section
      id={id}
      className={`lp-section-full flex w-full flex-col items-center justify-center py-24 ${
        background === 'gray' ? 'bg-[var(--lp-photo-surface)]' : 'bg-white'
      }`}
    >
      <div
        className={`mx-auto flex w-full max-w-[1440px] flex-col items-center justify-center gap-14 px-8 lg:px-[72px] ${
          reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'
        }`}
      >
        {/* 텍스트 컬럼 */}
        <div className="flex w-full max-w-[556px] shrink-0 flex-col items-start gap-10">
          <p className="lp-kicker">{kicker}</p>

          <h2 className="text-[28px] font-bold leading-[1.3] text-[var(--lp-heading)] sm:text-[34px] lg:text-[40px]">
            {headline.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h2>

          <div className="flex w-full flex-col items-start gap-4">
            <Point tone="before">{before}</Point>
            <Point tone="after">{after}</Point>
          </div>

          <button
            type="button"
            onClick={onStart}
            className="flex items-center gap-1.5 text-base font-semibold leading-6 text-[var(--lp-brand)] transition-opacity hover:opacity-75"
          >
            스튜디오 입장하기
            <img src="/landing/ic-arrow-sm.svg" alt="" className="size-4" />
          </button>
        </div>

        {/* 이미지 컬럼 — export가 시안 실치수(636·656·556·555 등)라 lg에서는 원본 크기로 둔다.
            좁은 화면에서는 컬럼 폭에 맞춰 축소한다. */}
        <div className="flex w-full items-center justify-center lg:w-auto lg:shrink-0">
          <img
            src={image.src}
            alt={image.alt}
            className="h-auto w-full max-w-full lg:w-auto lg:max-w-none"
          />
        </div>
      </div>
    </section>
  );
}
